import { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, MessageCircle, Phone, X, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { LIVE_CHAT_URL, PHONE_NUMBER, PHONE_TEL, WHATSAPP_URL } from '@/config/site';
import WhatsAppAccessModal from '@/components/ui/WhatsAppAccessModal';
import { shouldOpenWhatsAppDirectly } from '@/lib/device';

const CALLBACK_TOPICS = [
  'New Appointment',
  'Specialist Referral',
  'Treatment Question',
  'Aftercare Support',
];

export default function ChatBubble() {
  const [isOpen, setIsOpen] = useState(false);
  const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState(false);
  const [isCallbackExpanded, setIsCallbackExpanded] = useState(false);
  const [callbackTopic, setCallbackTopic] = useState('');
  const [callbackPhone, setCallbackPhone] = useState('');
  const [isCallbackSubmitting, setIsCallbackSubmitting] = useState(false);

  useEffect(() => {
    const handleToggle = () => setIsOpen((prev) => !prev);
    window.addEventListener('toggle-chat-bubble', handleToggle);
    return () => window.removeEventListener('toggle-chat-bubble', handleToggle);
  }, []);

  const handleWhatsAppClick = (event) => {
    if (shouldOpenWhatsAppDirectly()) return;
    event.preventDefault();
    setIsWhatsAppModalOpen(true);
  };

  const submitCallbackRequest = async (event) => {
    event.preventDefault();

    const topic = callbackTopic.trim();
    const phone = callbackPhone.trim();

    if (!topic) {
      toast.error('Select a reason first.');
      return;
    }

    if (!phone) {
      toast.error('Add your phone number.');
      return;
    }

    const apiBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    const endpoint = `${apiBase}/api/callback-requests`;

    setIsCallbackSubmitting(true);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          topic,
          phone,
          source: 'chat-bubble-desktop',
          page: window.location.pathname,
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result?.detail || 'Unable to request callback right now.');
      }

      toast.success('Callback requested', {
        description: 'Our team will call you shortly.',
      });

      setCallbackTopic('');
      setCallbackPhone('');
      setIsCallbackExpanded(false);
    } catch (error) {
      toast.error('Callback request failed', {
        description: error.message || 'Please try again or call us directly.',
      });
    } finally {
      setIsCallbackSubmitting(false);
    }
  };

  return (
    <div className="fixed left-3 right-3 bottom-20 z-30 md:left-auto md:right-8 md:bottom-8 md:w-[22rem] md:z-50" data-testid="chat-bubble-container">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.2 }}
            data-testid="chat-popup"
            className="absolute bottom-0 right-0 w-full overflow-hidden rounded-[1.35rem] border border-accent-gold/35 bg-gradient-to-b from-white via-white to-stone-50 shadow-[0_30px_70px_rgba(20,17,16,0.38)] ring-1 ring-charcoal/10 backdrop-blur-sm md:bottom-20"
          >
            {/* Header */}
            <div className="relative overflow-hidden bg-gradient-to-r from-charcoal via-charcoal to-charcoal-light p-4">
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-accent-gold/50" />
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading text-warm-white text-lg">Get in Touch</h3>
                  <p className="font-body text-stone-300 text-sm">Same-day response for specialist referrals</p>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  data-testid="chat-close-btn"
                  className="text-stone-400 hover:text-warm-white transition-colors"
                  aria-label="Close chat"
                >
                  <X size={20} strokeWidth={1.5} />
                </button>
              </div>
            </div>

            {/* Options */}
            <div className="p-4 space-y-3">
              <p className="font-body text-sm text-stone-600 mb-4">
                <span className="block">Need urgent triage?</span>
                <span className="block">Choose your fastest channel:</span>
              </p>

              {/* Live Chat Option */}
              <a
                href={LIVE_CHAT_URL}
                data-testid="chat-live-btn"
                className="group flex items-center gap-4 rounded-xl border border-stone-200 bg-white/90 p-4 shadow-sm transition-all hover:border-accent-gold/70 hover:bg-white"
              >
                <div className="w-12 h-12 rounded-full bg-charcoal flex items-center justify-center">
                  <Send size={20} className="text-warm-white" strokeWidth={1.5} />
                </div>
                <div>
                  <h4 className="font-body font-medium text-charcoal">Live Chat</h4>
                  <p className="font-body text-sm text-stone-500">Chat with our team now</p>
                </div>
              </a>

              {/* WhatsApp Option */}
              <a
                href={WHATSAPP_URL}
                onClick={handleWhatsAppClick}
                data-testid="chat-whatsapp-btn"
                className="group flex items-center gap-4 rounded-xl border border-stone-200 bg-white/90 p-4 shadow-sm transition-all hover:border-[#25D366] hover:bg-white"
              >
                <div className="w-12 h-12 rounded-full bg-[#25D366] flex items-center justify-center">
                  <MessageCircle size={20} className="text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <h4 className="font-body font-medium text-charcoal">WhatsApp</h4>
                  <p className="font-body text-sm text-stone-500">Message us on WhatsApp</p>
                </div>
              </a>

              {/* Mobile Call Option */}
              <a
                href={PHONE_TEL}
                data-testid="chat-call-btn"
                className="flex items-center gap-4 rounded-xl border border-stone-200 bg-white/90 p-4 shadow-sm transition-all hover:border-accent-gold/70 hover:bg-white md:hidden"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-charcoal flex items-center justify-center">
                    <Phone size={20} className="text-warm-white" strokeWidth={1.5} />
                  </div>
                </div>
                <div>
                  <h4 className="font-body font-medium text-charcoal">Call Us</h4>
                  <p className="font-body text-sm text-stone-500">{PHONE_NUMBER}</p>
                </div>
              </a>

              {/* Desktop Callback Option */}
              <div className="hidden rounded-xl border border-stone-200 bg-white/90 p-4 shadow-sm md:block">
                <button
                  type="button"
                  onClick={() => setIsCallbackExpanded((prev) => !prev)}
                  className="flex w-full items-start gap-4 text-left"
                >
                  <div className="w-12 h-12 rounded-full bg-stone-900 flex items-center justify-center">
                    <Phone size={20} className="text-warm-white" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-body font-medium text-charcoal">Request Callback</h4>
                    <p className="font-body text-sm text-stone-500">
                      {isCallbackExpanded ? 'Select a reason, then add your number.' : 'Click to request a callback.'}
                    </p>
                  </div>
                  {isCallbackExpanded ? (
                    <ChevronUp size={18} className="mt-1 text-stone-500" />
                  ) : (
                    <ChevronDown size={18} className="mt-1 text-stone-500" />
                  )}
                </button>

                {isCallbackExpanded && (
                  <div className="mt-3 border-t border-stone-200 pt-3">
                    <div className="grid grid-cols-2 gap-2">
                      {CALLBACK_TOPICS.map((topic) => (
                        <button
                          key={topic}
                          type="button"
                          onClick={() => setCallbackTopic(topic)}
                          className={`rounded-full border px-3 py-2 font-body text-xs transition-colors ${
                            callbackTopic === topic
                              ? 'border-charcoal bg-charcoal text-warm-white'
                              : 'border-stone-300 bg-white text-stone-600'
                          }`}
                        >
                          {topic}
                        </button>
                      ))}
                    </div>

                    {callbackTopic && (
                      <form onSubmit={submitCallbackRequest} className="mt-3 space-y-2">
                        <label htmlFor="callback-phone" className="block font-body text-xs font-medium text-stone-600">
                          Best number to call
                        </label>
                        <input
                          id="callback-phone"
                          type="tel"
                          value={callbackPhone}
                          onChange={(event) => setCallbackPhone(event.target.value)}
                          placeholder="e.g. 07513 660154"
                          className="h-11 w-full rounded-full border border-stone-300 bg-white px-4 font-body text-sm text-charcoal outline-none transition-colors focus:border-accent-gold"
                          autoComplete="tel"
                        />
                        <button
                          type="submit"
                          data-testid="chat-callback-btn"
                          disabled={isCallbackSubmitting}
                          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-charcoal px-4 py-3 font-body text-sm font-medium text-warm-white transition-colors hover:bg-charcoal-light disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isCallbackSubmitting ? 'Sending...' : 'Request Callback'}
                        </button>
                      </form>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 pb-4">
              <p className="font-body text-center text-xs text-stone-500">
                Available Mon-Fri, 9am-6pm
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Toggle Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        data-testid="chat-toggle-btn"
        className={`ml-auto hidden h-14 w-14 items-center justify-center rounded-full border shadow-[0_14px_35px_rgba(20,17,16,0.38)] ring-2 ring-white/75 transition-colors md:flex ${
          isOpen
            ? 'border-stone-300 bg-warm-white text-charcoal hover:bg-stone-100'
            : 'border-accent-gold/80 bg-accent-gold text-charcoal hover:bg-accent-gold-light'
        }`}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? (
          <X size={24} strokeWidth={1.5} />
        ) : (
          <MessageCircle size={24} strokeWidth={1.5} />
        )}
      </motion.button>

      <WhatsAppAccessModal
        open={isWhatsAppModalOpen}
        onClose={() => setIsWhatsAppModalOpen(false)}
      />
    </div>
  );
}
